import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtIDR, fmtShortIDR, fmtDate, firstOfMonthISO, todayISO } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, PieChart, Pie,
} from "recharts";
import {
  TrendingUp, TrendingDown, AlertTriangle, Plus, Trash2, DollarSign, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";

const CATS = [
  { v: "salary", l: "Gaji & THR" },
  { v: "marketing", l: "Marketing & Iklan" },
  { v: "rent", l: "Sewa Tempat" },
  { v: "utilities", l: "Listrik, Air, Internet" },
  { v: "supplies", l: "Bahan Habis Pakai" },
  { v: "equipment", l: "Alat & Maintenance" },
  { v: "tax", l: "Pajak" },
  { v: "commission", l: "Komisi Dokter/Therapist" },
  { v: "other", l: "Lainnya" },
];
const CAT_COLORS = [
  "hsl(0 72% 51%)", "hsl(24 95% 53%)", "hsl(43 89% 50%)", "hsl(142 71% 45%)",
  "hsl(173 58% 39%)", "hsl(197 71% 52%)", "hsl(217 91% 60%)", "hsl(271 76% 53%)", "hsl(340 75% 55%)",
];

export default function Finance() {
  const { branchId, branches } = useBranch();
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [pnl, setPnl] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ branch_id: "", category: "salary", amount: "", description: "" });

  const fetchAll = () => {
    const params = { branch_id: branchId, date_from: dateFrom, date_to: dateTo };
    api.get("/finance/pnl", { params }).then((r) => setPnl(r.data));
    api.get("/expenses", { params }).then((r) => setExpenses(r.data));
  };

  useEffect(() => { fetchAll(); }, [branchId, dateFrom, dateTo]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.branch_id || !form.amount) { toast.error("Cabang & amount wajib"); return; }
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense tercatat");
      setDialogOpen(false);
      setForm({ branch_id: "", category: "salary", amount: "", description: "" });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus expense ini?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Expense dihapus");
    fetchAll();
  };

  const pieData = (pnl?.breakdown || []).filter((b) => b.amount > 0);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Keuangan / Profit & Loss</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Laporan rahasia: revenue vs cost dengan alert dan rekomendasi mitigasi.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dari</label>
            <Input type="date" data-testid="fin-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sampai</label>
            <Input type="date" data-testid="fin-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[160px]" />
          </div>
          <Button data-testid="add-expense-btn" onClick={() => setDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Catat Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testId="fin-revenue" label="Revenue" value={pnl ? fmtShortIDR(pnl.revenue) : "—"} icon={DollarSign} accent="emerald" sublabel={pnl ? `${pnl.tx_count} transaksi` : ""} />
        <StatCard testId="fin-expense" label="Total Beban" value={pnl ? fmtShortIDR(pnl.total_expense) : "—"} icon={TrendingDown} accent="rose" />
        <StatCard testId="fin-net" label="Net Profit" value={pnl ? fmtShortIDR(pnl.net_profit) : "—"} icon={pnl?.net_profit >= 0 ? TrendingUp : TrendingDown} accent={pnl?.net_profit >= 0 ? "emerald" : "rose"} />
        <StatCard testId="fin-margin" label="Margin" value={pnl ? `${pnl.margin_pct}%` : "—"} icon={ShieldAlert} accent={pnl?.margin_pct >= 30 ? "emerald" : pnl?.margin_pct >= 15 ? "amber" : "rose"} />
      </div>

      {pnl?.alerts?.length > 0 && (
        <Card className="border-rose-200 bg-rose-50/40" data-testid="alerts-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2 text-rose-800">
              <AlertTriangle className="h-4 w-4" /> {pnl.alerts.length} Peringatan Biaya
            </CardTitle>
            <CardDescription>Action item mitigasi untuk masing-masing komponen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pnl.alerts.map((a, idx) => (
              <div key={idx} className="rounded-md border border-border bg-card p-4" data-testid={`alert-${a.category}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-sm">{a.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.pct}% dari revenue {a.threshold_pct ? `• threshold ${a.threshold_pct}%` : ""}
                    </div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${a.severity === "high" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                    {a.severity === "high" ? "Tinggi" : "Menengah"}
                  </span>
                </div>
                <div className="mt-2 text-sm">💡 {a.action}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Breakdown Beban per Kategori</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pnl?.breakdown || []} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}jt`} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={150} />
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                  <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                    {(pnl?.breakdown || []).map((_, i) => (
                      <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Proporsi Beban</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="amount" nameKey="label" innerRadius={45} outerRadius={90}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Daftar Expense</CardTitle>
          <CardDescription>{expenses.length} pengeluaran tercatat pada periode ini.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Deskripsi</TableHead>
                <TableHead>Cabang</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.slice(0, 200).map((e) => {
                const cat = CATS.find((c) => c.v === e.category);
                const br = branches.find((b) => b.id === e.branch_id);
                return (
                  <TableRow key={e.id} data-testid={`expense-${e.id}`}>
                    <TableCell className="text-xs">{fmtDate(e.paid_at)}</TableCell>
                    <TableCell className="text-sm">{cat?.l || e.category}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.description}</TableCell>
                    <TableCell className="text-xs">{br?.name?.replace("MyClinic ", "") || "—"}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{fmtIDR(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" data-testid={`delete-expense-${e.id}`} onClick={() => remove(e.id)} className="text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {expenses.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">Belum ada expense.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Catat Expense Baru</DialogTitle>
            <DialogDescription>Input pengeluaran. Akan otomatis tercatat di P&L.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Cabang *</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="exp-branch-select"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kategori *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-cat-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (Rp) *</Label>
              <Input type="number" min="0" step="1" data-testid="exp-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Input data-testid="exp-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="cth. Gaji dokter Juni" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="exp-save-btn">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
